// @ts-nocheck
// HC Interview Videos — V2 one-row header + V1 present-video two-column stage.
//
// Behavior:
//   - Auto-advance ticker via requestAnimationFrame
//   - Hover-pause only on devices with real hover (desktop), never on touch
//   - Static green slab + static rose accent (no per-slide colour flip)
//   - Slide regions (header, video, text) all toggle .is-active in sync
//   - Up-next progress bar reflects time-to-next-slide
//   - Lightbox opens on play-button click; ESC, click-outside, or close-btn dismiss
//   - Manual prev/next/up-next/arrow-key/swipe resets the cycle on the new slide
//
// A11y:
//   - prefers-reduced-motion disables auto-advance; manual nav still works
//   - ArrowLeft / ArrowRight advance slides when section has focus
//   - Touch swipe (horizontal-dominant) advances slides; vertical scroll preserved
//   - Lightbox traps Tab/Shift+Tab; ESC closes; focus returns to play button
//   - aria-live announcer narrates slide changes for screen readers

(() => {
  const REDUCED_MOTION = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Touch devices fire a sticky mouseenter on tap but never the matching
  // mouseleave — would freeze auto-advance in "paused on hover". Only wire
  // hover-pause where the primary input actually supports hover.
  const HAS_HOVER = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover)').matches;

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'video[controls]',
    'audio[controls]',
  ].join(',');

  // All slide-region attributes that should toggle .is-active together
  // (header is static now — only the video + text columns swap per slide)
  const SLIDE_REGIONS = [
    'data-hc-video-slide',
    'data-hc-text-slide',
  ];

  function initInterviewVideos(root) {
    if (!root || root.dataset.hcInit === '1') return;
    root.dataset.hcInit = '1';

    const slab = root.querySelector('[data-hc-slab]');
    const stage = root.querySelector('[data-hc-stage]'); // == slab
    const status = root.querySelector('[data-hc-status]');
    const announcer = root.querySelector('[data-hc-announcer]');
    const lightbox = root.querySelector('[data-hc-lightbox]');
    const lightboxFrame = root.querySelector('[data-hc-lightbox-frame]');
    const lightboxClose = root.querySelector('[data-hc-lightbox-close]');

    const N = parseInt(root.dataset.blockCount, 10) || 0;
    const AUTO_MS = Math.max(1000, parseInt(root.dataset.autoAdvanceMs, 10) || 7000);
    const AUTO_SECS = Math.round(AUTO_MS / 1000);

    if (!slab || N === 0) return;

    // Make the section programmatically focusable so keyboard listeners have a host
    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');

    let active = 0;
    let paused = false;
    let lightboxOpen = false;
    let cycleStart = 0;
    let storedProgress = 0;
    let rafId = 0;
    let escListener = null;
    let trapListener = null;
    let previouslyFocused = null;

    const isRunning = () => !REDUCED_MOTION && !paused && !lightboxOpen && N > 1;

    // ── Progress visuals (up-next bar) ───────────────────────────────────────

    function paintProgress(progress) {
      const activeText = root.querySelector('[data-hc-text-slide].is-active');
      if (activeText) {
        const bar = activeText.querySelector('[data-hc-up-next-progress]');
        if (bar) bar.style.transform = `scaleX(${progress})`;
      }
    }

    function resetProgressVisuals() {
      root.querySelectorAll('[data-hc-up-next-progress]').forEach(b => {
        b.style.transform = 'scaleX(0)';
      });
    }

    function updateStatusLabel() {
      if (!status) return;
      if (REDUCED_MOTION) {
        status.textContent = '◼ manual';
      } else if (paused || lightboxOpen) {
        status.textContent = '◼ paused on hover';
      } else if (N > 1) {
        status.textContent = `▷ auto · ${AUTO_SECS}s`;
      }
    }

    // ── Ticker ───────────────────────────────────────────────────────────────

    function tick(now) {
      rafId = 0;
      if (!isRunning()) return;
      const progress = Math.min(1, (now - cycleStart) / AUTO_MS);
      paintProgress(progress);
      if (progress >= 1) {
        advanceTo(active + 1);
        return;
      }
      rafId = requestAnimationFrame(tick);
    }

    function scheduleRaf() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
      if (isRunning()) rafId = requestAnimationFrame(tick);
    }

    function startCycle() {
      cycleStart = performance.now();
      storedProgress = 0;
      paintProgress(0);
      scheduleRaf();
      updateStatusLabel();
    }

    function pauseCycle() {
      if (rafId) {
        storedProgress = Math.min(1, (performance.now() - cycleStart) / AUTO_MS);
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      updateStatusLabel();
    }

    function resumeCycle() {
      if (!isRunning()) {
        updateStatusLabel();
        return;
      }
      cycleStart = performance.now() - (storedProgress * AUTO_MS);
      scheduleRaf();
      updateStatusLabel();
    }

    // ── Slide switching ─────────────────────────────────────────────────────

    function announceActive() {
      if (!announcer) return;
      const activeText = root.querySelector('[data-hc-text-slide].is-active');
      if (!activeText) return;
      const nameEl = activeText.querySelector('.hc-interview-videos__person-name');
      const metaEl = activeText.querySelector('.hc-interview-videos__person-meta');
      const name = nameEl ? nameEl.textContent.trim().replace(/\s+/g, ' ') : '';
      const meta = metaEl ? metaEl.textContent.trim().split('·')[0].trim() : '';
      const idx = active + 1;
      const msg = meta
        ? `Interview ${idx} of ${N}: ${name}, ${meta}.`
        : `Interview ${idx} of ${N}: ${name}.`;
      announcer.textContent = '';
      // eslint-disable-next-line no-unused-expressions
      announcer.offsetHeight;
      announcer.textContent = msg;
    }

    function toggleActiveSlides() {
      SLIDE_REGIONS.forEach(attr => {
        root.querySelectorAll(`[${attr}]`).forEach(el => {
          const isMatch = parseInt(el.dataset.blockIndex, 10) === active;
          el.classList.toggle('is-active', isMatch);
          // Update aria-hidden so SRs ignore inactive content
          if (isMatch) el.removeAttribute('aria-hidden');
          else el.setAttribute('aria-hidden', 'true');
        });
      });
    }

    function advanceTo(nextIdx, opts) {
      if (N <= 1) return;
      active = ((nextIdx % N) + N) % N;
      toggleActiveSlides();
      resetProgressVisuals();
      if (REDUCED_MOTION) {
        updateStatusLabel();
      } else {
        startCycle();
      }
      if (!opts || opts.announce !== false) announceActive();
    }

    // ── Hover pause (desktop only) ──────────────────────────────────────────

    if (slab && N > 1 && !REDUCED_MOTION && HAS_HOVER) {
      slab.addEventListener('mouseenter', () => {
        paused = true;
        pauseCycle();
      });
      slab.addEventListener('mouseleave', () => {
        paused = false;
        resumeCycle();
      });
    }

    // ── Keyboard navigation ─────────────────────────────────────────────────

    if (N > 1) {
      root.addEventListener('keydown', (e) => {
        if (lightboxOpen) return;
        const tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (e.target && e.target.isContentEditable) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          advanceTo(active - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          advanceTo(active + 1);
        }
      });
    }

    // ── Touch swipe (horizontal-dominant) ───────────────────────────────────

    if (stage && N > 1) {
      const SWIPE_THRESHOLD = 50;
      let touchStartX = 0;
      let touchStartY = 0;
      let touchActive = false;

      stage.addEventListener('touchstart', (e) => {
        if (!e.touches || e.touches.length !== 1) { touchActive = false; return; }
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchActive = true;
      }, { passive: true });

      stage.addEventListener('touchend', (e) => {
        if (!touchActive) return;
        touchActive = false;
        const t = (e.changedTouches && e.changedTouches[0]) || null;
        if (!t) return;
        const dx = t.clientX - touchStartX;
        const dy = t.clientY - touchStartY;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;
        if (Math.abs(dy) > Math.abs(dx)) return;
        if (dx < 0) advanceTo(active + 1);
        else advanceTo(active - 1);
      }, { passive: true });

      stage.addEventListener('touchcancel', () => { touchActive = false; }, { passive: true });
    }

    // ── Lightbox ────────────────────────────────────────────────────────────

    function getVideoSources(blockId) {
      const node = root.querySelector(`script[data-hc-video-data][data-block-id="${blockId}"]`);
      if (!node) return [];
      try {
        const data = JSON.parse(node.textContent);
        return Array.isArray(data.sources) ? data.sources : [];
      } catch (_) {
        return [];
      }
    }

    function getFocusableInLightbox() {
      if (!lightbox) return [];
      return Array.from(lightbox.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(el => !el.hasAttribute('hidden') && el.offsetParent !== null);
    }

    function trapTab(e) {
      if (e.key !== 'Tab') return;
      const focusables = getFocusableInLightbox();
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey) {
        if (activeEl === first || !lightbox.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function openLightbox(blockId, trigger) {
      if (!lightbox || !lightboxFrame) return;
      lightboxFrame.innerHTML = '';

      const sources = getVideoSources(blockId);
      if (sources.length > 0) {
        const video = document.createElement('video');
        video.controls = true;
        video.playsInline = true;
        video.autoplay = true;
        video.preload = 'metadata';
        sources.forEach(s => {
          const src = document.createElement('source');
          src.src = s.url;
          if (s.mime_type) src.type = s.mime_type;
          video.appendChild(src);
        });
        lightboxFrame.appendChild(video);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'hc-interview-videos__lightbox-placeholder';
        placeholder.textContent = 'Video coming soon.';
        lightboxFrame.appendChild(placeholder);
      }

      lightbox.hidden = false;
      document.body.classList.add('hc-interview-videos--lightbox-open');

      lightboxOpen = true;
      pauseCycle();

      previouslyFocused = trigger || document.activeElement;
      if (lightboxClose && typeof lightboxClose.focus === 'function') {
        lightboxClose.focus();
      }

      escListener = (e) => { if (e.key === 'Escape') closeLightbox(); };
      trapListener = (e) => trapTab(e);
      document.addEventListener('keydown', escListener);
      document.addEventListener('keydown', trapListener);
    }

    function closeLightbox() {
      if (!lightbox || !lightboxFrame) return;
      const video = lightboxFrame.querySelector('video');
      if (video) { video.pause(); video.removeAttribute('src'); video.load(); }
      lightboxFrame.innerHTML = '';
      lightbox.hidden = true;
      document.body.classList.remove('hc-interview-videos--lightbox-open');

      lightboxOpen = false;
      paused = HAS_HOVER && !!(slab && slab.matches(':hover'));
      resumeCycle();

      if (escListener) {
        document.removeEventListener('keydown', escListener);
        escListener = null;
      }
      if (trapListener) {
        document.removeEventListener('keydown', trapListener);
        trapListener = null;
      }

      if (previouslyFocused && typeof previouslyFocused.focus === 'function'
          && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
      previouslyFocused = null;
    }

    // ── Event wiring ────────────────────────────────────────────────────────

    root.querySelectorAll('[data-hc-play-button]').forEach(btn => {
      btn.addEventListener('click', () => openLightbox(btn.dataset.blockId, btn));
    });

    if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
    if (lightbox) {
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox) closeLightbox();
      });
    }

    const prev = root.querySelector('[data-hc-prev]');
    const next = root.querySelector('[data-hc-next]');
    if (prev) prev.addEventListener('click', () => advanceTo(active - 1));
    if (next) next.addEventListener('click', () => advanceTo(active + 1));

    root.querySelectorAll('[data-hc-up-next]').forEach(btn => {
      btn.addEventListener('click', () => {
        const target = parseInt(btn.dataset.targetIndex, 10);
        if (!Number.isNaN(target)) advanceTo(target);
      });
    });

    // ── Init ────────────────────────────────────────────────────────────────

    resetProgressVisuals();
    if (N > 1 && !REDUCED_MOTION) {
      startCycle();
    } else {
      updateStatusLabel();
    }
  }

  document.querySelectorAll('[data-hc-interview-videos]').forEach(initInterviewVideos);

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target && e.target.querySelector
      ? e.target.querySelector('[data-hc-interview-videos]')
      : null;
    if (root) initInterviewVideos(root);
  });

  document.addEventListener('shopify:section:unload', () => {
    if (document.body.classList.contains('hc-interview-videos--lightbox-open')) {
      document.body.classList.remove('hc-interview-videos--lightbox-open');
    }
  });
})();
